/* One analysis, from the dropped file to the last check.
 *
 * BRIEF.md defect 6 is that value sits behind work which serves the engine: a
 * parse to confirm, then a "Run checks" button, then four table confirmations,
 * before a single finding is visible. Step 12 removes the whole staircase.
 * Reading the document and checking it become one movement, and the reviewer is
 * taken to the workspace the moment there is something in it to read.
 *
 * That is why this lives outside React. The run starts on /new and has to
 * survive the navigation to /review/:docId — a run owned by the page that
 * started it would be cancelled by its own success. So the run is a plain
 * object with subscribers: /new renders its progress, the workspace renders its
 * findings, and neither owns it.
 *
 * Nothing here reaches the network. `ingest` is injected rather than imported
 * so that this module can be exercised outside a browser — pdf.js is not
 * importable under node — and so the caller decides what is being read.
 */

import { runDeterministic, sortFlags } from '../engine/deterministic';
import { putDocument, putFindings, setLastDocumentId } from './store';
import type {
  CheckFailure,
  CheckSkip,
  Flag,
  IngestProgress,
  IngestRejection,
  IngestResult,
  SourceDocument,
} from '../types';

/* ==========================================================================
   The named steps
   --------------------------------------------------------------------------
   A progress bar that says "working" tells a reviewer nothing about whether to
   wait. These are the stages of the actual pipeline, named as what they do to
   the document rather than as what the code is called.
   ========================================================================== */

export type RunStepId =
  | 'reading'
  | 'structure'
  | 'references'
  | 'arithmetic'
  | 'patterns';

export type RunStepState = 'waiting' | 'running' | 'done' | 'skipped' | 'failed';

export interface RunStep {
  id: RunStepId;
  /** What the reviewer reads. Carries its own counts: "Reading pages (14 of 35)". */
  label: string;
  state: RunStepState;
  /** Why it did not happen. Present only on 'skipped' and 'failed'. */
  note?: string;
}

export const STEP_ORDER: RunStepId[] = [
  'reading',
  'structure',
  'references',
  'arithmetic',
  'patterns',
];

const STEP_LABELS: Record<RunStepId, string> = {
  reading: 'Reading pages',
  structure: 'Finding sections and tables',
  references: 'Checking references',
  arithmetic: 'Running arithmetic checks',
  patterns: 'Matching clause patterns',
};

/**
 * The clause-pattern stage is the one stage that would send text off this
 * machine, and the page the reviewer dropped the file on promises it never
 * leaves. So it is not run as part of an ingest, and it says so by name rather
 * than by being absent from the list — a stage that did not happen must never
 * look like a stage that found nothing.
 */
export const PATTERNS_CHECK_ID = 'model-classification';

export const PATTERNS_NOT_RUN =
  'Not run. Matching clauses by pattern is the one stage that would send text out of this browser, so Ceiling never does it as part of reading a document.';

const PATTERNS_LOOKED_FOR =
  'The eleven checks the arithmetic cannot reach — stipulated values, behavioural savings, undefined change thresholds and the rest — matched against the clause language of the plan. That stage reads the document with a language model, which means sending it somewhere. Nothing in this review has been sent anywhere.';

/**
 * The one skip every review carries, whichever path produced it: the run
 * started on /new, and the fallback run the workspace does for a document that
 * was read before its checks were stored.
 */
export const PATTERNS_SKIP: CheckSkip = {
  checkId: PATTERNS_CHECK_ID,
  reason: PATTERNS_NOT_RUN,
  lookedFor: PATTERNS_LOOKED_FOR,
};

/** "Reading pages (14 of 35)", or just "Reading pages" before the count exists. */
export function stepLabel(id: RunStepId, counts: RunCounts): string {
  const base = STEP_LABELS[id];
  if (id === 'reading' && counts.pageCount) {
    return `${base} (${counts.page ?? 0} of ${counts.pageCount})`;
  }
  if (id === 'structure' && counts.sections !== undefined) {
    return `${base} (${counts.sections} sections, ${counts.tables ?? 0} tables)`;
  }
  if (id === 'references' && counts.references !== undefined) {
    return `${base} (${counts.references})`;
  }
  if (id === 'arithmetic' && counts.checksRun !== undefined) {
    return `${base} (${counts.checksRun} of ${counts.checksTotal ?? counts.checksRun})`;
  }
  return base;
}

export interface RunCounts {
  page?: number;
  pageCount?: number;
  sections?: number;
  tables?: number;
  references?: number;
  checksRun?: number;
  checksTotal?: number;
}

/** How many checks the arithmetic pass will settle, for the "n of m" line. */
const ARITHMETIC_CHECK_COUNT = 5;

/* ==========================================================================
   The run
   ========================================================================== */

export type RunStatus =
  /** Reading and checking. */
  | 'running'
  /** Every stage settled. */
  | 'done'
  /** The file was refused before anything was read. */
  | 'rejected'
  /** Something stopped mid-way. Whatever was found is kept. */
  | 'failed';

export interface AnalysisRunState {
  /** Null until ingestion has produced a document. */
  docId: string | null;
  fileName: string;
  status: RunStatus;
  steps: RunStep[];
  counts: RunCounts;
  /** The parse, held in memory so the workspace need not race storage for it. */
  document: SourceDocument | null;
  /** In the order the finished run would put them, at every point during it. */
  flags: Flag[];
  ranChecks: string[];
  skipped: CheckSkip[];
  failures: CheckFailure[];
  /** Set when status is 'rejected'. */
  rejection?: IngestRejection;
  /** Set when status is 'failed': what stopped, in plain language. */
  error?: string;
}

interface AnalysisRun {
  readonly state: AnalysisRunState;
  subscribe(listener: (state: AnalysisRunState) => void): () => void;
}

interface MutableRun extends AnalysisRun {
  state: AnalysisRunState;
}

function initialSteps(): RunStep[] {
  return STEP_ORDER.map((id) => ({
    id,
    label: STEP_LABELS[id],
    state: 'waiting' as RunStepState,
  }));
}

function initialState(fileName: string): AnalysisRunState {
  return {
    docId: null,
    fileName,
    status: 'running',
    steps: initialSteps(),
    counts: {},
    document: null,
    flags: [],
    ranChecks: [],
    skipped: [],
    failures: [],
  };
}

/**
 * The run this browser has going, if any. One at a time: a reviewer reads one
 * document at a time, and two runs writing findings at once is a race with
 * nothing to gain from it.
 */
let active: MutableRun | null = null;

/** The run for this document, or null when there is none in flight. */
export function runFor(docId: string): AnalysisRun | null {
  if (!active || active.state.docId !== docId) return null;
  return active;
}

/** The run in flight, whatever document it turns out to be for. */
export function activeRun(): AnalysisRun | null {
  return active;
}

/**
 * Forgets a finished run. Called by the workspace once it has taken what the
 * run produced, so that a second visit reads storage rather than a stale copy
 * of the same findings.
 */
export function clearRun(docId: string): void {
  if (active && active.state.docId === docId && active.state.status !== 'running') {
    active = null;
  }
}

interface StartOptions {
  /** What the reviewer will see named while the file is being read. */
  fileName: string;
  /** Produces the parse. Injected so this module never imports pdf.js. */
  ingest(onProgress: (p: IngestProgress) => void): Promise<IngestResult>;
  /** Off in the check harness, which has no IndexedDB to write to. */
  persist?: boolean;
}

/**
 * Reads a document and checks it, publishing as it goes.
 *
 * Returns as soon as the run has been started; the caller watches it through
 * `subscribe`. Nothing throws out of here: an exception anywhere becomes a
 * 'failed' state carrying whatever had been found by then, because a reviewer
 * who has been shown four findings must not lose them to the fifth check.
 */
export function startAnalysis(options: StartOptions): AnalysisRun {
  const listeners = new Set<(state: AnalysisRunState) => void>();

  const run: MutableRun = {
    state: initialState(options.fileName),
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
  active = run;

  const publish = (patch: Partial<AnalysisRunState>) => {
    run.state = { ...run.state, ...patch };
    for (const listener of listeners) listener(run.state);
  };

  /** Rewrites one step, and relabels it from the counts it now has. */
  const step = (id: RunStepId, state: RunStepState, note?: string) => {
    publish({
      steps: run.state.steps.map((s) =>
        s.id === id
          ? { ...s, state, label: stepLabel(id, run.state.counts), ...(note ? { note } : {}) }
          : s,
      ),
    });
  };

  const count = (patch: RunCounts) => {
    const counts = { ...run.state.counts, ...patch };
    publish({
      counts,
      steps: run.state.steps.map((s) => ({ ...s, label: stepLabel(s.id, counts) })),
    });
  };

  void (async () => {
    try {
      /* --- reading and parsing ------------------------------------------ */

      step('reading', 'running');

      const result = await options.ingest((progress: IngestProgress) => {
        if (progress.phase === 'extracting') {
          count({ page: progress.page, pageCount: progress.pageCount });
        } else if (progress.phase === 'parsing') {
          step('reading', 'done');
          step('structure', 'running');
        }
      });

      if (!result.ok) {
        publish({ status: 'rejected', rejection: result.rejection });
        return;
      }

      const doc = result.document;
      count({
        page: doc.pageCount,
        pageCount: doc.pageCount,
        sections: doc.sections.length,
        tables: doc.tables.length,
        references: doc.citations.length,
        checksTotal: ARITHMETIC_CHECK_COUNT,
        checksRun: 0,
      });
      publish({ docId: doc.id, document: doc, fileName: doc.fileName });
      step('reading', 'done');
      step('structure', 'done');
      step('references', 'done');

      // Written before the checks so that a reviewer who reloads mid-run still
      // has the document, and so the workspace can read the parse from storage
      // if it arrives after this run has been forgotten.
      if (options.persist !== false) {
        try {
          await putDocument(doc);
          await setLastDocumentId(doc.id);
        } catch {
          // Persisting is a convenience, never a precondition. The run holds
          // the document in memory either way.
        }
      }

      /* --- the checks ---------------------------------------------------- */

      step('arithmetic', 'running');

      const run1 = await runDeterministic(doc, {
        awardDate: doc.metadata.awardDate,
        onCheck: (event) => {
          const counts = {
            checksRun: (run.state.counts.checksRun ?? 0) + 1,
            checksTotal: ARITHMETIC_CHECK_COUNT,
          };
          const patch: Partial<AnalysisRunState> = {};
          if (event.outcome === 'ran') {
            patch.ranChecks = [...run.state.ranChecks, event.checkId];
            if (event.flags.length > 0) {
              patch.flags = sortFlags([...run.state.flags, ...event.flags]);
            }
          } else if (event.outcome === 'skipped' && event.skip) {
            patch.skipped = [...run.state.skipped, event.skip];
          } else if (event.outcome === 'failed' && event.failure) {
            patch.failures = [...run.state.failures, event.failure];
          }
          publish(patch);
          count(counts);
        },
      });

      step('arithmetic', 'done');

      // The stage that is never run, named rather than hidden.
      publish({
        flags: sortFlags(run1.flags),
        ranChecks: run1.ranChecks,
        skipped: [...run1.skipped, PATTERNS_SKIP],
        failures: run1.failures,
      });
      step('patterns', 'skipped', PATTERNS_NOT_RUN);

      if (options.persist !== false) {
        try {
          await putFindings({
            docId: doc.id,
            createdAt: new Date().toISOString(),
            flags: run.state.flags,
            ranChecks: run.state.ranChecks,
            skipped: run.state.skipped,
          });
        } catch {
          // The findings are on screen either way.
        }
      }

      publish({ status: 'done' });
    } catch (error) {
      // Whatever was found stays found. The reviewer is told what stopped and
      // what that leaves them with, and never shown the exception.
      console.error('[analysis] the run stopped before it finished', error);
      publish({
        status: 'failed',
        error:
          run.state.document === null
            ? 'Reading the document stopped before it finished, so no checks ran against it. Nothing left your browser. Trying the file again, or re-exporting it from whatever produced it, usually works.'
            : 'The checks stopped before they all finished. What was found before that point is below and is unaffected — every finding there carries its own arithmetic. What is missing is whatever the remaining checks would have found, which is not the same as their having found nothing.',
      });
      if (run.state.document && options.persist !== false) {
        try {
          await putFindings({
            docId: run.state.document.id,
            createdAt: new Date().toISOString(),
            flags: run.state.flags,
            ranChecks: run.state.ranChecks,
            skipped: run.state.skipped,
          });
        } catch {
          // As above.
        }
      }
    }
  })();

  return run;
}

/* ==========================================================================
   Reading a run
   ========================================================================== */

/** True once there is something in the workspace worth navigating to. */
export function readyToOpen(state: AnalysisRunState): boolean {
  if (state.docId === null) return false;
  return state.flags.length > 0 || state.status === 'done' || state.status === 'failed';
}

/** "Still checking… (3 found so far)". The line the rail shows mid-run. */
export function stillCheckingLine(found: number): string {
  return `Still checking… (${found} found so far)`;
}
